import { brand } from '../../lib/brand';
/**
 * @page SetupWizardPage
 * @description System initialization wizard — venue info + admin account in one step
 * Shown only on first boot when system_initialized is false.
 * Uses dedicated POST /settings/setup (no JWT required).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSiteName } from '../../hooks/useSiteName';

type Step = 'form' | 'done';

export default function SetupWizardPage() {
  const navigate = useNavigate();
  const siteName = useSiteName();
  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState('');

  // Guard: redirect if already initialized
  const { data: initData } = useQuery({
    queryKey: ['settings', 'initialized'],
    queryFn: () => api.get<{ initialized: boolean }>('/settings/initialized'),
  });
  useEffect(() => {
    if (initData?.initialized) navigate('/login', { replace: true });
  }, [initData, navigate]);

  // Form fields
  const [venueName, setVenueName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const setupMutation = useMutation({
    mutationFn: async () => {
      if (password !== confirmPassword) throw new Error('Passwords do not match');
      if (password.length < 8) throw new Error('Password must be at least 8 characters');
      await api.post('/settings/setup', { venueName, username, password });
    },
    onSuccess: () => {
      setStep('done');
      setError('');
    },
    onError: (err: Error) => setError(err.message),
  });

  const canSubmit =
    venueName.trim() && username.trim() && password && confirmPassword && !setupMutation.isPending;

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100 p-6">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold text-center text-base-content mb-2">{siteName}</h1>
        <p className="text-center text-base-content/60 mb-8">System Setup</p>

        {error && (
          <div className="mb-4 px-4 py-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
            {error}
          </div>
        )}

        {step === 'form' && (
          <div className="bg-base-200 rounded-xl border border-base-300 p-6 space-y-5">
            {/* Venue info */}
            <div>
              <h2 className="text-lg font-bold mb-3">Venue Information</h2>
              <label className="block text-sm text-base-content/70 mb-1.5">Venue Name *</label>
              <input
                value={venueName}
                onChange={(e) => setVenueName(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
                placeholder={brand.name}
              />
            </div>

            <div className="border-t border-base-300" />

            {/* Admin account */}
            <div className="space-y-3">
              <h2 className="text-lg font-bold">Admin Account</h2>
              <div>
                <label className="block text-sm text-base-content/70 mb-1.5">Username *</label>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm text-base-content/70 mb-1.5">
                  Password * (min 8 chars)
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm text-base-content/70 mb-1.5">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <button
              onClick={() => setupMutation.mutate()}
              disabled={!canSubmit}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-content font-medium disabled:opacity-50"
            >
              {setupMutation.isPending ? 'Setting up...' : 'Complete Setup'}
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="bg-base-200 rounded-xl border border-base-300 p-6 text-center space-y-4">
            <div className="text-4xl text-success">&#10003;</div>
            <h2 className="text-lg font-bold">Setup Complete!</h2>
            <p className="text-sm text-base-content/60">
              Your system is ready. Please log in with your new admin account.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-content font-medium"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
